import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { 
  ChevronLeft, 
  ChevronRight, 
  DragIndicator,
  Reorder 
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DriveEtaIcon from "@mui/icons-material/DriveEta";

// ✅ FIX: px-based page sizes for strict screen containment
const pageSizeMap = {
  A4:        { width: "794px",  minHeight: "1123px" },
  Letter:    { width: "816px",  minHeight: "1056px" },
  Legal:     { width: "816px",  minHeight: "1344px" },
  A3:        { width: "1123px", minHeight: "1587px" },
  Executive: { width: "696px",  minHeight: "1008px" },
};

const defaultLeftSectionOrder = [
  'profile', 'workExperience', 'volunteering',
  'education', 'certifications', 'accomplishments', 'additionalInfo'
];

const defaultRightSectionOrder = [
  'contact', 'skills', 'languages', 'interests', 'references'
];

// ✅ Common safe text style
const safeText = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  minWidth: 0,
};

const Resume7 = ({
  theme = { primary: "#0951b0ff", dark: "#063a80" },
  color = "#0951b0ff",
  font = "EB Garamond",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "24px",
  sectionSpacing = 10,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 20,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const defaultData = {
    firstName: "John", lastName: "Doe",
    currentPosition: "Full Stack Developer",
    phone: "+91 9876543210", email: "johndoe@email.com",
    city: "Chennai, India", website: "www.johndoe.com",
    linkedIn: "", drivingLicence: "",
    profile: "Highly motivated Full Stack Developer with experience in building scalable MERN stack applications.",
  };
  const data = { ...defaultData, ...formData };

  const currentTheme = {
    mainBg:   "#b3e4e6ff",
    sideBg:   color || theme?.primary || "#0951b0ff",
    highlight:color || theme?.primary || "#0951b0ff",
    text:     "#000000",
    sideText: "#ffffff",
  };

  const globalFontStyle = {
    fontFamily: font, fontSize, fontStyle, lineHeight: `${lineSpacing}px`,
  };
  const getTextStyle = (extra = {}) => ({ ...globalFontStyle, ...safeText, ...extra });

  // ─── State ────────────────────────────────────────────────────────────────────
  const [educationEntries, setEducationEntries]         = useState([]);
  const [savedSkills, setSavedSkills]                   = useState([]);
  const [savedSummary, setSavedSummary]                 = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo]   = useState([]);
  const [savedLanguages, setSavedLanguages]             = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications]   = useState([]);
  const [savedReferences, setSavedReferences]           = useState([]);
  const [softwareSkills, setSoftwareSkills]             = useState([]);
  const [savedVolunteering, setSavedVolunteering]       = useState([]);
  const [savedInterests, setSavedInterests]             = useState([]);
  const [savedWebsites, setSavedWebsites]               = useState([]);
  const [customSections, setCustomSections]             = useState({});

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages]             = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const rightContentRef  = useRef(null);
  const sidebarContentRef = useRef(null);

  const [leftSectionOrder,  setLeftSectionOrder]  = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide,  setDragOverSide]  = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ─── Data Loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const se = localStorage.getItem("educationEntries"); if (se) setEducationEntries(JSON.parse(se));
      const ss = localStorage.getItem("skills");           if (ss) setSavedSkills(JSON.parse(ss));

      const sums = JSON.parse(localStorage.getItem("summaries") || "[]");
      if (sums.length > 0) setSavedSummary(sums[sums.length - 1]);

      setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")     || "[]"));
      setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")            || "[]"));
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")|| "[]"));
      setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList")  || "[]"));
      setSavedReferences(JSON.parse(localStorage.getItem("referencesList")          || "[]"));
      setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")           || "[]"));
      setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")          || "[]"));
      setSavedInterests(JSON.parse(localStorage.getItem("interests")                || "[]"));
      setSavedWebsites(JSON.parse(localStorage.getItem("websites")                  || "[]"));

      // Custom sections
      const userId = localStorage.getItem("userId");
      let sectionsData = {};
      if (userId) {
        const current = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`) || "[]");
        current.forEach(name => {
          try {
            const raw = localStorage.getItem(`custom_${userId}_${name}`);
            if (raw) {
              const parsed = JSON.parse(raw);
              const valid = (parsed.items || []).filter(i => i && i.trim() !== '');
              if (valid.length > 0) sectionsData[name] = valid;
            }
          } catch (e) {}
        });
      }
      setCustomSections(sectionsData);

      // Section orders
      const sl = localStorage.getItem("resume7LeftSectionOrder");
      if (sl) {
        try { setLeftSectionOrder(JSON.parse(sl).filter(s => !s.startsWith('custom_'))); }
        catch { setLeftSectionOrder(defaultLeftSectionOrder); }
      }

      const sr = localStorage.getItem("resume7RightSectionOrder");
      const customIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      if (sr) {
        try {
          const parsed = JSON.parse(sr).filter(s => !s.startsWith('custom_'));
          setRightSectionOrder([...parsed, ...customIds]);
        } catch { setRightSectionOrder([...defaultRightSectionOrder, ...customIds]); }
      } else {
        setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (leftSectionOrder.length > 0)  localStorage.setItem("resume7LeftSectionOrder",  JSON.stringify(leftSectionOrder));
  }, [leftSectionOrder]);
  useEffect(() => {
    if (rightSectionOrder.length > 0) localStorage.setItem("resume7RightSectionOrder", JSON.stringify(rightSectionOrder));
  }, [rightSectionOrder]);

  const formatDate = (month, year) => (!month || !year) ? "" : `${month} ${year}`;

  // ─── Drag & Drop ──────────────────────────────────────────────────────────────
  const handleLeftDragStart  = (e, i) => { e.dataTransfer.setData('text/plain', JSON.stringify({ index:i, side:'left',  sectionId:leftSectionOrder[i]  })); e.currentTarget.style.opacity='0.5'; };
  const handleRightDragStart = (e, i) => { e.dataTransfer.setData('text/plain', JSON.stringify({ index:i, side:'right', sectionId:rightSectionOrder[i] })); e.currentTarget.style.opacity='0.5'; };
  const handleDragOver  = (e, i, side) => { e.preventDefault(); setDragOverIndex(i); setDragOverSide(side); };
  const handleDragLeave = (e)           => { e.preventDefault(); setDragOverIndex(null); setDragOverSide(null); };
  const handleDragEnd   = (e)           => { setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity='1'; };

  const handleLeftDrop = (e, ti) => {
    e.preventDefault();
    const { index:si, side:ss, sectionId } = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (ss==='left' && si!==ti) { const a=[...leftSectionOrder]; const [m]=a.splice(si,1); a.splice(ti,0,m); setLeftSectionOrder(a); }
    else if (ss==='right') { const l=[...leftSectionOrder],r=[...rightSectionOrder]; r.splice(si,1); l.splice(ti,0,sectionId); setLeftSectionOrder(l); setRightSectionOrder(r); }
    setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity='1';
  };
  const handleRightDrop = (e, ti) => {
    e.preventDefault();
    const { index:si, side:ss, sectionId } = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (ss==='right' && si!==ti) { const a=[...rightSectionOrder]; const [m]=a.splice(si,1); a.splice(ti,0,m); setRightSectionOrder(a); }
    else if (ss==='left') { const l=[...leftSectionOrder],r=[...rightSectionOrder]; l.splice(si,1); r.splice(ti,0,sectionId); setLeftSectionOrder(l); setRightSectionOrder(r); }
    setDragOverIndex(null); setDragOverSide(null); e.currentTarget.style.opacity='1';
  };

  const resetLeftSectionOrder  = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => { const ids=Object.keys(customSections).map(n=>`custom_${n}`); setRightSectionOrder([...defaultRightSectionOrder,...ids]); };
  const resetAllSectionOrder   = () => { resetLeftSectionOrder(); resetRightSectionOrder(); };

  const getSectionTitle = (id) => {
    const titles = { contact:'Contact', skills:'Skills', languages:'Languages', interests:'Interests', references:'References', profile:'Profile', workExperience:'Work Experience', volunteering:'Volunteering', education:'Education', certifications:'Certifications', accomplishments:'Accomplishments', additionalInfo:'Additional Information' };
    if (id.startsWith('custom_')) { const n=id.replace('custom_','').replace(/_/g,' '); return n.charAt(0).toUpperCase()+n.slice(1); }
    return titles[id] || id;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb:3 }}>
      <Typography variant="h6" sx={{ mb:2, display:'flex', alignItems:'center' }}>
        <DragIndicator sx={{ mr:1 }} />
        {side==='left' ? 'Left Content' : 'Right Sidebar'} Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml:1 }}>({sections.length} sections)</Typography>
      </Typography>
      <Box sx={{ maxHeight:400, overflow:'auto', border:'1px solid #e0e0e0', borderRadius:1, p:1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper key={sectionId} draggable
            onDragStart={(e) => side==='left' ? handleLeftDragStart(e,index) : handleRightDragStart(e,index)}
            onDragOver={(e) => handleDragOver(e,index,side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => side==='left' ? handleLeftDrop(e,index) : handleRightDrop(e,index)}
            onDragEnd={handleDragEnd}
            sx={{ p:2, mb:1, display:'flex', alignItems:'center', backgroundColor:dragOverIndex===index&&dragOverSide===side?'#f0f0f0':'white', boxShadow:dragOverIndex===index&&dragOverSide===side?2:1, cursor:'grab', '&:active':{cursor:'grabbing'}, transition:'all 0.2s ease' }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml:1, flexGrow:1 }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index+1}</Typography>
          </Paper>
        )) : (
          <Typography variant="body2" color="textSecondary" sx={{ p:2, textAlign:'center' }}>No sections. Drag from the other side.</Typography>
        )}
      </Box>
      <Button onClick={side==='left'?resetLeftSectionOrder:resetRightSectionOrder} variant="outlined" size="small" sx={{ mt:1 }}>
        Reset {side==='left'?'Left':'Right'} Order
      </Button>
    </Box>
  );

  // ─── Section heading helper ───────────────────────────────────────────────────
  const SectionHeading = ({ label }) => (
    <Box sx={{ bgcolor:currentTheme.highlight, color:"#fff", p:1, mb:`${sectionSpacing/2}px`, overflow:'hidden', boxSizing:'border-box' }}>
      <Typography variant="h6" fontWeight="bold" sx={{ fontSize:headingSize, fontFamily:font, fontStyle, ...safeText }}>{label}</Typography>
    </Box>
  );

  // ─── Left Content Sections ────────────────────────────────────────────────────
  const renderLeftContentSections = () => leftSectionOrder.map(sectionId => {
    switch (sectionId) {
      case 'profile':
        return (
          <div key="profile" className="resume-section">
            <Box sx={{ bgcolor:'black', p:3, mb:`${sectionSpacing}px`, overflow:'hidden', boxSizing:'border-box' }}>
              <Typography variant="body1" sx={{ color:"#fff", fontSize, lineHeight:`${lineSpacing}px`, mb:`${paragraphSpacing}px`, fontFamily:font, fontStyle, ...safeText }}>
                {savedSummary || data.profile}
              </Typography>
            </Box>
          </div>
        );

      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className="resume-section">
            <SectionHeading label="Professional Experience" />
            <Box p={2} sx={{ mb:`${sectionSpacing}px` }}>
              {workExperiences.map((work, i) => (
                <Box key={i} sx={{ mb:`${paragraphSpacing}px` }}>
                  {/* ✅ FIX: flexWrap so date never overflows */}
                  <Box sx={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:0.5 }}>
                    <Typography fontWeight="bold" sx={{ fontSize:`calc(${fontSize} * 1.1)`, lineHeight:`${lineSpacing}px`, fontFamily:font, fontStyle, flex:'1 1 auto', ...safeText }}>
                      {work.employer} @ {work.jobTitle}
                    </Typography>
                    <Typography sx={{ fontSize, fontFamily:font, fontStyle:'italic', flexShrink:0 }}>
                      {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize, mb:`${paragraphSpacing}px`, fontFamily:font, fontStyle:'italic', ...safeText }}>
                    {work.companyName}{work.location && ` | ${work.location}`}
                  </Typography>
                  {work.description && (
                    <Box
                      sx={{ mt:1, ...globalFontStyle, ...safeText,
                        "& strong":{ fontWeight:"bold" }, "& em":{ fontStyle:"italic" }, "& u":{ textDecoration:"underline" } }}
                      dangerouslySetInnerHTML={{ __html: work.description
                        .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family:${font};font-size:${fontSize};font-style:${fontStyle}">$1</strong>`)
                        .replace(/\*(.*?)\*/g, `<em style="font-family:${font};font-size:${fontSize}">$1</em>`)
                        .replace(/<u>(.*?)<\/u>/g, `<u style="font-family:${font};font-size:${fontSize};font-style:${fontStyle}">$1</u>`)
                        .replace(/\n/g, "<br/>")
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className="resume-section">
            <SectionHeading label="Volunteering Experience" />
            <Box p={2} sx={{ mb:`${sectionSpacing}px` }}>
              {savedVolunteering.map((vol, i) => (
                <Box key={i} sx={{ mb:2 }}>
                  <Box sx={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:0.5 }}>
                    <Typography sx={{ fontWeight:"bold", fontSize, fontFamily:font, fontStyle, flex:'1 1 auto', ...safeText }}>{vol.subtopic}</Typography>
                    <Typography sx={{ fontSize, fontStyle:'italic', fontFamily:font, flexShrink:0 }}>{vol.fromDate} - {vol.toDate}</Typography>
                  </Box>
                  <Typography sx={{ fontSize, fontFamily:font, fontStyle, mt:0.5, ...safeText }}>{vol.content}</Typography>
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className="resume-section">
            <SectionHeading label="Education" />
            <Box p={2}>
              {educationEntries.map((edu, i) => {
                const institutionName = edu.educationLevel==="school" ? edu.schoolName : edu.collegeName;
                const degreeName      = edu.educationLevel==="school" ? edu.standard   : edu.degreeName;
                const fieldOfStudy    = edu.educationLevel==="school" ? edu.stream     : edu.specialization;
                const location        = edu.educationLevel==="school" ? edu.schoolLocation : edu.collegeLocation;
                return (
                  <Box key={i} sx={{ mb:`${paragraphSpacing}px` }}>
                    <Box sx={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:0.5 }}>
                      <Typography fontWeight="bold" sx={{ fontSize:`calc(${fontSize} * 1.1)`, lineHeight:`${lineSpacing}px`, fontFamily:font, fontStyle, flex:'1 1 auto', ...safeText }}>
                        {degreeName}{fieldOfStudy && ` - ${fieldOfStudy}`}
                      </Typography>
                      <Typography sx={{ fontSize, fontFamily:font, fontStyle:'italic', flexShrink:0 }}>{formatDate(edu.gradMonth, edu.gradYear)}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontSize, fontFamily:font, fontStyle, ...safeText }}>
                      {institutionName}{location && `, ${location}`}{edu.district && `, ${edu.district}`}
                    </Typography>
                    {(edu.percentage || edu.cgpa || edu.grade) && (
                      <Typography variant="body2" sx={{ fontSize, fontFamily:font, fontStyle, ...safeText }}>
                        {edu.percentage && `Percentage: ${edu.percentage}%`}
                        {edu.cgpa && ` | CGPA: ${edu.cgpa}`}
                        {edu.grade && ` | Grade: ${edu.grade}`}
                      </Typography>
                    )}
                    {edu.additionalCoursework && (
                      <Typography sx={{ fontSize, fontFamily:font, fontStyle, ...safeText }}>{edu.additionalCoursework}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className="resume-section">
            <SectionHeading label="Certifications" />
            <Box p={2}>
              {savedCertifications.map((cert, i) => (
                <Typography key={i} sx={{ fontSize, mb:`${paragraphSpacing/2}px`, fontFamily:font, fontStyle, ...safeText }}>
                  {cert.name} – {cert.provider} ({cert.year})
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className="resume-section">
            <SectionHeading label="Accomplishments" />
            <Box p={2}>
              {savedAccomplishments.map((acc, i) => (
                <Typography key={i} sx={{ fontSize, mb:`${paragraphSpacing/2}px`, fontFamily:font, fontStyle, ...safeText }} whiteSpace="pre-line">• {acc}</Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className="resume-section">
            <SectionHeading label="Additional Information" />
            <Box p={2}>
              {savedAdditionalInfo.map((info, i) => (
                <Typography key={i} sx={{ fontSize, mb:`${paragraphSpacing/2}px`, fontFamily:font, fontStyle, ...safeText }} whiteSpace="pre-line">• {info}</Typography>
              ))}
            </Box>
          </div>
        ) : null;

      // Sections moved from right side
      case 'contact':
        return (
          <div key="contact-left" className="resume-section">
            <SectionHeading label="Contact" />
            <Box p={2}>
              <Avatar src={photo||"/profile.jpg"} sx={{ width:100, height:100, mx:"auto", mb:2, border:`${lineWeight}px solid ${currentTheme.sideBg}` }} />
              {renderContactItems(currentTheme.highlight, false)}
            </Box>
          </div>
        );

      case 'skills':
        return (savedSkills.length>0||softwareSkills.length>0) ? (
          <div key="skills-left" className="resume-section">
            <SectionHeading label="Key Skills" />
            <Box p={2}>
              {[...savedSkills, ...softwareSkills].map((s,i) => (
                <Typography key={i} sx={{ fontSize, mb:`${paragraphSpacing/2}px`, fontFamily:font, fontStyle, ...safeText }}>
                  • {s.name}{(s.rating||s.level) ? ` (${s.rating||s.level}%)` : ''}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length>0 ? (
          <div key="languages-left" className="resume-section">
            <SectionHeading label="Languages" />
            <Box p={2}>
              {savedLanguages.map((lang,i) => (
                <Typography key={i} sx={{ fontSize, mb:`${paragraphSpacing/2}px`, fontFamily:font, fontStyle, ...safeText }}>
                  • {lang.name}{lang.level ? ` - ${lang.level}` : ''}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length>0 ? (
          <div key="interests-left" className="resume-section">
            <SectionHeading label="Interests" />
            <Box p={2}>
              {savedInterests.map((interest,i) => (
                <Typography key={i} sx={{ fontSize, mb:`${paragraphSpacing/2}px`, fontFamily:font, fontStyle, ...safeText }}>• {interest}</Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'references':
        return savedReferences.length>0 ? (
          <div key="references-left" className="resume-section">
            <SectionHeading label="References" />
            <Box p={2}>
              {savedReferences.map((ref,i) => (
                <Typography key={i} sx={{ fontSize, mb:`${paragraphSpacing/2}px`, fontFamily:font, fontStyle, ...safeText }}>• {ref}</Typography>
              ))}
            </Box>
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const name = sectionId.replace('custom_','');
          const items = customSections[name] || [];
          return items.length>0 ? (
            <div key={sectionId} className="resume-section">
              <SectionHeading label={name.replace(/_/g,' ').toUpperCase()} />
              <Box p={2}>
                {items.map((item,i) => (
                  <Typography key={i} sx={{ fontSize, mb:`${paragraphSpacing/2}px`, fontFamily:font, fontStyle, ...safeText }} whiteSpace="pre-line">• {item}</Typography>
                ))}
              </Box>
            </div>
          ) : null;
        }
        return null;
    }
  }).filter(Boolean);

  // ─── Contact items helper (reused in sidebar + left) ─────────────────────────
  const renderContactItems = (iconColor, centered = true) => {
    const justify = centered ? 'center' : 'flex-start';
    return (
      <>
        {data.phone && (
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:justify, gap:1, mb:1, flexWrap:'wrap' }}>
            <PhoneIcon sx={{ fontSize:16, color:iconColor, flexShrink:0 }} />
            <Typography sx={{ fontSize, fontFamily:font, ...safeText }}>{data.phone}</Typography>
          </Box>
        )}
        {data.email && (
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:justify, gap:1, mb:1, flexWrap:'wrap' }}>
            <EmailIcon sx={{ fontSize:16, color:iconColor, flexShrink:0 }} />
            <Typography sx={{ fontSize, fontFamily:font, wordBreak:'break-all' }}>{data.email}</Typography>
          </Box>
        )}
        {data.city && (
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:justify, gap:1, mb:1, flexWrap:'wrap' }}>
            <LocationOnIcon sx={{ fontSize:16, color:iconColor, flexShrink:0 }} />
            <Typography sx={{ fontSize, fontFamily:font, ...safeText }}>{data.city}</Typography>
          </Box>
        )}
        {data.website && (
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:justify, gap:1, mb:1, flexWrap:'wrap' }}>
            <LanguageIcon sx={{ fontSize:16, color:iconColor, flexShrink:0 }} />
            <a href={data.website.startsWith('http')?data.website:`https://${data.website}`} target="_blank" rel="noopener noreferrer"
              style={{ color:iconColor, textDecoration:'none', fontFamily:font, fontSize, wordBreak:'break-all' }}>
              {data.website.replace(/^https?:\/\/(www\.)?/,'')}
            </a>
          </Box>
        )}
        {data.linkedIn && (
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:justify, gap:1, mb:1, flexWrap:'wrap' }}>
            <LinkedInIcon sx={{ fontSize:16, color:'#0077b5', flexShrink:0 }} />
            <a href={data.linkedIn.startsWith('http')?data.linkedIn:`https://${data.linkedIn}`} target="_blank" rel="noopener noreferrer"
              style={{ color:iconColor==='#ffffff'?'#fff':'#0077b5', textDecoration:'none', fontFamily:font, fontSize, wordBreak:'break-all' }}>
              {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//,'')}
            </a>
          </Box>
        )}
        {data.drivingLicence && (
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:justify, gap:1, mb:1 }}>
            <DriveEtaIcon sx={{ fontSize:16, color:'#4caf50', flexShrink:0 }} />
            <Typography sx={{ fontSize, fontFamily:font }}>DL: {data.drivingLicence}</Typography>
          </Box>
        )}
        {savedWebsites.map((site,i) => (
          <Box key={i} sx={{ display:'flex', alignItems:'center', justifyContent:justify, gap:1, mb:1, flexWrap:'wrap' }}>
            <LanguageIcon sx={{ fontSize:16, color:iconColor, flexShrink:0 }} />
            <a href={site.url.startsWith('http')?site.url:`https://${site.url}`} target="_blank" rel="noopener noreferrer"
              style={{ color:iconColor, textDecoration:'none', fontFamily:font, fontSize, wordBreak:'break-all' }}>
              {site.name||site.url}
            </a>
          </Box>
        ))}
      </>
    );
  };

  // ─── Sidebar Sections ─────────────────────────────────────────────────────────
  const renderSidebarSections = () => rightSectionOrder.map(sectionId => {
    const SideHeading = ({ label }) => (
      <Typography variant="h6" fontWeight="bold" sx={{ color:currentTheme.sideText, mb:2, fontSize:headingSize, fontFamily:font, fontStyle, ...safeText }}>{label}</Typography>
    );

    switch (sectionId) {
      case 'contact':
        return (
          <div key="contact" className="sidebar-section">
            <Avatar src={photo||"/profile.jpg"} sx={{ width:140, height:140, mx:"auto", mb:2, border:`${lineWeight}px solid ${currentTheme.highlight}`, flexShrink:0 }} />
            <Box sx={{ color:currentTheme.sideText, textAlign:'center', mb:2 }}>
              {renderContactItems(currentTheme.sideText, true)}
            </Box>
            <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
          </div>
        );

      case 'skills':
        return (savedSkills.length>0||softwareSkills.length>0) ? (
          <div key="skills" className="sidebar-section">
            <SideHeading label="Key Skills" />
            <List dense disablePadding>
              {[...savedSkills,...softwareSkills].map((s,i) => (
                <ListItem key={i} sx={{ py:0, pl:`${paragraphIndent}px` }}>
                  <ListItemText primary={`• ${s.name}${(s.rating||s.level)?` (${s.rating||s.level}%)`:''}`}
                    primaryTypographyProps={{ sx:{ ...getTextStyle(), color:currentTheme.sideText } }} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length>0 ? (
          <div key="languages" className="sidebar-section">
            <SideHeading label="Languages" />
            <List dense disablePadding>
              {savedLanguages.map((lang,i) => (
                <ListItem key={i} sx={{ py:0, pl:`${paragraphIndent}px` }}>
                  <ListItemText primary={`• ${lang.name}${lang.level?` - ${lang.level}`:''}`}
                    primaryTypographyProps={{ sx:{ ...getTextStyle(), color:currentTheme.sideText } }} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length>0 ? (
          <div key="interests" className="sidebar-section">
            <SideHeading label="Interests" />
            <List dense disablePadding>
              {savedInterests.map((interest,i) => (
                <ListItem key={i} sx={{ py:0, pl:`${paragraphIndent}px` }}>
                  <ListItemText primary={`• ${interest}`}
                    primaryTypographyProps={{ sx:{ ...getTextStyle(), color:currentTheme.sideText } }} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
          </div>
        ) : null;

      case 'references':
        return savedReferences.length>0 ? (
          <div key="references" className="sidebar-section">
            <SideHeading label="References" />
            <List dense disablePadding>
              {savedReferences.map((ref,i) => (
                <ListItem key={i} sx={{ py:0, pl:`${paragraphIndent}px` }}>
                  <ListItemText primary={`• ${ref}`}
                    primaryTypographyProps={{ sx:{ ...getTextStyle(), color:currentTheme.sideText } }} />
                </ListItem>
              ))}
            </List>
          </div>
        ) : null;

      // Sections moved from left
      case 'profile':
        return (
          <div key="profile-sidebar" className="sidebar-section">
            <SideHeading label="Profile" />
            <Typography sx={{ ...getTextStyle({ mb:2 }), color:currentTheme.sideText }}>{savedSummary||data.profile}</Typography>
            <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
          </div>
        );

      case 'workExperience':
        return workExperiences&&workExperiences.length>0 ? (
          <div key="workExperience-sidebar" className="sidebar-section">
            <SideHeading label="Work Experience" />
            {workExperiences.slice(0,2).map((work,i) => (
              <Box key={i} sx={{ mb:2 }}>
                <Typography fontWeight="bold" sx={{ ...getTextStyle({ fontSize:`calc(${fontSize}*0.9)` }), color:currentTheme.sideText }}>{work.jobTitle}</Typography>
                <Typography sx={{ ...getTextStyle({ fontSize:`calc(${fontSize}*0.85)`, fontStyle:'italic' }), color:currentTheme.sideText }}>{work.employer}</Typography>
                <Typography sx={{ ...getTextStyle({ fontSize:`calc(${fontSize}*0.85)` }), color:currentTheme.sideText }}>
                  {formatDate(work.startMonth,work.startYear)} – {work.current?"Present":formatDate(work.endMonth,work.endYear)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
          </div>
        ) : null;

      case 'education':
        return educationEntries.length>0 ? (
          <div key="education-sidebar" className="sidebar-section">
            <SideHeading label="Education" />
            {educationEntries.slice(0,2).map((edu,i) => (
              <Box key={i} sx={{ mb:2 }}>
                <Typography fontWeight="bold" sx={{ ...getTextStyle({ fontSize:`calc(${fontSize}*0.9)` }), color:currentTheme.sideText }}>
                  {edu.educationLevel==="school"?edu.standard:edu.degreeName}
                </Typography>
                <Typography sx={{ ...getTextStyle({ fontSize:`calc(${fontSize}*0.85)` }), color:currentTheme.sideText }}>
                  {edu.educationLevel==="school"?edu.schoolName:edu.collegeName}
                </Typography>
                <Typography sx={{ ...getTextStyle({ fontSize:`calc(${fontSize}*0.85)` }), color:currentTheme.sideText }}>
                  {formatDate(edu.gradMonth,edu.gradYear)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length>0 ? (
          <div key="certifications-sidebar" className="sidebar-section">
            <SideHeading label="Certifications" />
            <List dense disablePadding>
              {savedCertifications.slice(0,3).map((cert,i) => (
                <ListItem key={i} sx={{ py:0, pl:`${paragraphIndent}px` }}>
                  <ListItemText primary={`• ${cert.name}`}
                    primaryTypographyProps={{ sx:{ ...getTextStyle(), color:currentTheme.sideText } }} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const name = sectionId.replace('custom_','');
          const items = customSections[name] || [];
          return items.length>0 ? (
            <div key={sectionId} className="sidebar-section">
              <SideHeading label={name.replace(/_/g,' ').toUpperCase()} />
              <List dense disablePadding>
                {items.slice(0,3).map((item,i) => (
                  <ListItem key={i} sx={{ py:0, pl:`${paragraphIndent}px` }}>
                    <ListItemText primary={`• ${item}`}
                      primaryTypographyProps={{ sx:{ ...getTextStyle(), color:currentTheme.sideText } }} />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ bgcolor:currentTheme.highlight, my:2, height:`${lineWeight}px` }} />
            </div>
          ) : null;
        }
        return null;
    }
  }).filter(Boolean);

  // ─── Header ───────────────────────────────────────────────────────────────────
  const renderMainHeader = () => (
    <div className="resume-section">
      <Box sx={{ bgcolor:currentTheme.highlight, color:"#fff", p:3, mb:`${sectionSpacing}px`, overflow:'hidden', boxSizing:'border-box' }}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize:`calc(${headingSize}*1.5)`, lineHeight:`${lineSpacing*2}px`, fontFamily:font, fontStyle, ...safeText }}>
          {data.firstName} {data.lastName}
        </Typography>
        <Typography variant="h6" sx={{ fontSize:`calc(${headingSize}*0.8)`, lineHeight:`${lineSpacing}px`, fontFamily:font, fontStyle, ...safeText }}>
          {data.currentPosition}
        </Typography>
      </Box>
    </div>
  );

  // ─── Pagination ───────────────────────────────────────────────────────────────
  // ✅ FIX: use numeric px height
  const pageHeightNum = parseInt(page.minHeight.replace('px',''), 10);
  const availableHeight = pageHeightNum - (2 * topBottomMargin) - 120; // 120 = approx header

  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;
    const sections = Array.from(sidebarContentRef.current.querySelectorAll('.sidebar-section'));
    let newPages=[], current=[], h=0;
    sections.forEach(s => {
      const sh = s.offsetHeight;
      if (h+sh > availableHeight && current.length > 0) { newPages.push([...current]); current=[s.outerHTML]; h=sh; }
      else { current.push(s.outerHTML); h+=sh; }
    });
    if (current.length>0) newPages.push([...current]);
    if (newPages.length===0) newPages.push([]);
    setSidebarPages(newPages);
  }, [page.minHeight, topBottomMargin, rightSectionOrder, availableHeight]);

  const paginateMainContent = useCallback(() => {
    if (!rightContentRef.current) return;
    const sections = Array.from(rightContentRef.current.querySelectorAll('.resume-section'));
    let newPages=[], current=[], h=0;
    sections.forEach(s => {
      const sh = s.offsetHeight;
      if (h+sh > availableHeight && current.length > 0) { newPages.push([...current]); current=[s.outerHTML]; h=sh; }
      else { current.push(s.outerHTML); h+=sh; }
    });
    if (current.length>0) newPages.push([...current]);
    if (newPages.length===0) newPages.push([]);
    setPages(newPages);
  }, [page.minHeight, topBottomMargin, leftSectionOrder, availableHeight]);

  useEffect(() => {
    const id = setTimeout(() => { paginateSidebarContent(); paginateMainContent(); }, 500);
    return () => clearTimeout(id);
  }, [
    paginateSidebarContent, paginateMainContent,
    educationEntries, savedSkills, savedSummary, workExperiences, savedAdditionalInfo,
    savedLanguages, savedAccomplishments, savedCertifications, savedReferences,
    softwareSkills, savedVolunteering, savedInterests, savedWebsites, customSections,
    leftSectionOrder, rightSectionOrder, color, font, fontSize, fontStyle, headingSize,
    sectionSpacing, paragraphSpacing, lineSpacing, topBottomMargin, sideMargins,
    paragraphIndent, lineWeight, pageSize,
  ]);

  // ─── Measurement boxes ────────────────────────────────────────────────────────
  const pageWidthNum = parseInt(page.width.replace('px',''), 10);

  const renderSidebarForMeasurement = () => (
    <Box ref={sidebarContentRef} sx={{
      visibility:'hidden', position:'absolute', left:'-9999px', top:0,
      width:`${pageWidthNum*0.30}px`, boxSizing:'border-box', overflow:'hidden',
      pt: `${topBottomMargin}px`,
      pb: `${topBottomMargin}px`,
      pl: `${sideMargins}px`,
      pr: `${sideMargins}px`,
      ...globalFontStyle
    }}>
      {renderSidebarSections()}
    </Box>
  );

  const renderMainContentForMeasurement = () => (
    <Box ref={rightContentRef} sx={{
      visibility:'hidden', position:'absolute', left:'-9999px', top:0,
      width:`${pageWidthNum*0.70}px`, boxSizing:'border-box', overflow:'hidden',
      pt: `${topBottomMargin}px`,
      pb: `${topBottomMargin}px`,
      pl: `${sideMargins}px`,
      pr: `${sideMargins}px`,
      ...globalFontStyle
    }}>
      {renderMainHeader()}
      {renderLeftContentSections()}
    </Box>
  );

  // ─── Page box styles ──────────────────────────────────────────────────────────
  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  const pageBoxStyles = {
    width: page.width,
    minHeight: page.minHeight,
    mx: "auto",
    bgcolor: currentTheme.mainBg,
    color: currentTheme.text,
    display: "flex",
    flexDirection: "row",
    overflow: 'hidden',          // ✅ KEY: nothing escapes A4
    boxSizing: 'border-box',
    pageBreakAfter: "always",
    "@media print": {
      width: page.width,
      minHeight: page.minHeight,
      boxShadow: "none",
      m: 0,
      overflow: 'hidden',
    },
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width:"100%", position:"relative", m:5 }}>
      {!exporting && (
        <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', mb:2, gap:2, '@media print':{ display:'none' } }}>
          <IconButton onClick={() => setCurrentPage(p=>Math.max(0,p-1))} disabled={currentPage===0} color="primary"><ChevronLeft /></IconButton>
          <Typography sx={{ mx:2 }}>Page {currentPage+1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(p=>Math.min(totalPages-1,p+1))} disabled={currentPage===totalPages-1} color="primary"><ChevronRight /></IconButton>
          {enableDragDrop && (
            <Box sx={{ display:'flex', gap:2, alignItems:'center', ml:2 }}>
              <Button onClick={()=>setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />} color="primary">Arrange Sections</Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">Reset All Sections</Button>
            </Box>
          )}
        </Box>
      )}

      {renderSidebarForMeasurement()}
      {renderMainContentForMeasurement()}

      <Box id="resume-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box key={pageIndex} className="resume-page" sx={{
            ...pageBoxStyles,
            boxShadow: !exporting && pageIndex===currentPage ? 5 : 0,
            display: exporting ? 'flex' : (pageIndex===currentPage ? 'flex' : 'none'),
            '@media print': { display:'flex' }
          }}>
            {/* ✅ LEFT MAIN CONTENT — 70%, strictly bounded */}
            <Box sx={{
              width: '70%',
              flexShrink: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
              minWidth: 0,
              bgcolor: currentTheme.mainBg,
              pt: `${topBottomMargin}px`,
              pb: `${topBottomMargin}px`,
              pl: `${sideMargins}px`,
              pr: `${sideMargins}px`,
              fontFamily: font, fontSize, fontStyle,
            }}>
              {pages[pageIndex] ? (
                <div style={{ wordBreak:'break-word', overflowWrap:'break-word', ...globalFontStyle }}
                  dangerouslySetInnerHTML={{ __html: pages[pageIndex].join('') }} />
              ) : null}
            </Box>

            {/* ✅ RIGHT SIDEBAR — 30%, strictly bounded */}
            <Box sx={{
              width: '30%',
              flexShrink: 0,
              boxSizing: 'border-box',
              overflow: 'hidden',
              minWidth: 0,
              bgcolor: currentTheme.sideBg,
              color: currentTheme.sideText,
              textAlign: 'center',
              pt: `${topBottomMargin}px`,
              pb: `${topBottomMargin}px`,
              pl: `${sideMargins}px`,
              pr: `${sideMargins}px`,
              fontFamily: font, fontSize, fontStyle,
              "@media print": {
                bgcolor: currentTheme.sideBg,
                color: currentTheme.sideText,
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact',
                pt: `${topBottomMargin}px`,
                pb: `${topBottomMargin}px`,
                pl: `${sideMargins}px`,
                pr: `${sideMargins}px`,
              },
            }}>
              {sidebarPages[pageIndex] ? (
                <div style={{ wordBreak:'break-word', overflowWrap:'break-word', ...globalFontStyle }}
                  dangerouslySetInnerHTML={{ __html: sidebarPages[pageIndex].join('') }} />
              ) : null}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={()=>setArrangeDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle><Box display="flex" alignItems="center"><Reorder sx={{ mr:1 }} />Arrange Resume Sections</Box></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb:3 }}>
            Drag and drop sections to reorder them or move between left content and right sidebar.
          </Typography>
          <Box sx={{ display:"flex", flexDirection:{ xs:"column", md:"row" }, gap:3, width:"100%" }}>
            <Box sx={{ flex:1, width:"50%" }}>{renderDragPanel("left",  leftSectionOrder)}</Box>
            <Box sx={{ flex:1, width:"50%" }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={()=>setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

export default Resume7;